<template>
  <!-- Indicador de andamento do OCR por PDF (novo motor assíncrono em lote). Enquanto o processo
       não conclui, mostra a ficha "em andamento" numa linha de status. Some/oculta quando ainda
       não há processo (NAO_AGENDADO). -->
  <div class="odin-ocr-pdf-progresso" v-if="visivel">

    <div class="mv-status" :class="'is-' + statusInfo.cls">
      <span class="mv-status__badge">
        <i class="fal mr-1" :class="statusInfo.icon"></i>{{ statusInfo.label }}
      </span>
      <span class="mv-status__msg">{{ statusMensagem }}</span>
      <span class="mv-status__desde" v-if="naFila && fila && fila.desde"
            title="Quando esta ficha entrou na fila do Asgard">
        <i class="fal fa-hourglass-start mr-1"></i>desde {{ fila.desde | datahora }}
      </span>
      <button v-if="naFila" type="button" class="mv-status__acao mv-status__acao--sair"
              :disabled="removendo" @click="removerDaFila"
              title="Tira o OCR desta ficha da fila. Nada foi processado ainda, então não há resultado a perder.">
        <i class="fal mr-1" :class="removendo ? 'fa-circle-notch fa-spin' : 'fa-xmark'"></i>{{ removendo ? 'Removendo…' : 'Remover da fila' }}
      </button>
      <button v-if="emAndamento" type="button" class="mv-status__acao"
              :disabled="executando" @click="executarAgora"
              :title="naFila ? 'Despacha o OCR agora, sem aguardar o processamento agendado'
                             : 'Atualiza o andamento do OCR agora'">
        <i class="fal mr-1" :class="executando ? 'fa-circle-notch fa-spin' : (naFila ? 'fa-bolt' : 'fa-arrows-rotate')"></i>{{ executando ? 'Processando…' : (naFila ? 'Executar agora' : 'Atualizar') }}
      </button>
    </div>

    <!-- O envio ao asgard-ia falhou e a fila segue retentando. Este bloco é o que faltava: o motivo
         já era gravado na linha da fila e só aparecia no painel de agendamentos — aqui a ficha
         continuava dizendo "aguardando na fila", sem sinal nenhum de que algo tinha dado errado. -->
    <div class="mv-falha" v-if="naFila && falhaDeDespacho">
      <div class="mv-falha__head">
        <i class="fal fa-triangle-exclamation mr-1"></i>
        O envio desta ficha ao Odin falhou em {{ falhaDeDespacho.em | datahora }}
      </div>
      <p class="mv-falha__txt">
        O sistema continua tentando de tempos em tempos. Se a mensagem abaixo se repetir por horas,
        avise o suporte — ou remova a ficha da fila e gere o OCR de novo.
      </p>
      <pre class="mv-falha__msg" v-if="falhaDeDespacho.motivo">{{ falhaDeDespacho.motivo }}</pre>
    </div>

  </div>
</template>

<script>
import OdinOcrPdfBusiness from "@/business/odin/OdinOcrPdfBusiness";
import FrontBusiness from "@/business/FrontBusiness";

// Estados crus vindos do backend (mesma máquina de estados do processamento assíncrono).
const RAW = {
  NAO_AGENDADO: 'NAO_AGENDADO',
  NA_FILA: 'NA_FILA',
  RECEIVED: 'RECEIVED',
  PROCESSING: 'PROCESSING',
  AWAITING_BATCH: 'AWAITING_BATCH',
  COMPLETED: 'COMPLETED',
  ERROR: 'ERROR',
};

export default {
  name: "OdinOcrPdfProgresso",
  props: {
    // id do indicador real (matrícula) ou do registro auxiliar.
    fichaId: {
      type: String,
      required: true,
    },
    // MATRICULA (indicador real) ou REGISTRO_AUXILIAR.
    livro: {
      type: String,
      default: 'MATRICULA',
    },
  },
  data() {
    return {
      status: null,               // estado cru atual (RAW.*)
      mensagemErro: null,
      // Detalhe da fila do Asgard, enviado pelo backend só no NA_FILA: posição, total, desde quando
      // espera, falha de despacho e fila parada. Null nos demais estados.
      fila: null,
      // Polling por setTimeout encadeado (sem sobreposição): o próximo poll só é agendado DEPOIS
      // que o atual termina. Idêntico ao padrão da Análise de Matrícula (ITN).
      poller: null,
      pollAtivo: false,
      consultando: false,
      pollTentativas: 0,
      executando: false,
      removendo: false,
    };
  },
  computed: {
    // Oculta enquanto não há processo agendado (NAO_AGENDADO) ou antes da 1ª resposta.
    visivel() {
      return !!this.status && this.status !== RAW.NAO_AGENDADO;
    },
    emAndamento() {
      return [RAW.NA_FILA, RAW.RECEIVED, RAW.PROCESSING, RAW.AWAITING_BATCH].includes(this.status);
    },
    // "Na fila" = o job de despacho ainda não rodou. É quando faz sentido o "Executar agora".
    naFila() {
      return this.status === RAW.NA_FILA;
    },
    // Despacho que já falhou: a linha continua pendente e é retentada, então o estado segue NA_FILA
    // indefinidamente. Sem este par, o laço de retentativa não tem sintoma nenhum na tela.
    falhaDeDespacho() {
      const f = this.fila || {};
      return f.falhouEm ? { em: f.falhouEm, motivo: f.motivoFalha } : null;
    },
    statusInfo() {
      // Fila da serventia parada (módulo Odin desligado ou teto de crédito): "Na fila" sugeria
      // espera normal, e essa não anda até alguém agir.
      if (this.naFila && this.fila && this.fila.parada) {
        return { cls: 'pend', icon: 'fa-circle-pause', label: 'Fila parada' };
      }
      const map = {
        [RAW.NA_FILA]: { cls: 'fila', icon: 'fa-clock', label: 'Na fila do Asgard' },
        [RAW.RECEIVED]: { cls: 'proc', icon: 'fa-arrows-rotate', label: 'Processando' },
        [RAW.PROCESSING]: { cls: 'proc', icon: 'fa-arrows-rotate', label: 'Processando' },
        [RAW.AWAITING_BATCH]: { cls: 'proc', icon: 'fa-layer-group', label: 'Processando em lote' },
        [RAW.COMPLETED]: { cls: 'ok', icon: 'fa-circle-check', label: 'OCR concluído' },
        [RAW.ERROR]: { cls: 'err', icon: 'fa-circle-exclamation', label: 'Falhou' },
      };
      return map[this.status] || { cls: 'fila', icon: 'fa-clock', label: 'Aguardando' };
    },
    statusMensagem() {
      // NA_FILA é a fila do ASGARD: o agendamento existe aqui e ainda não foi enviado ao Odin. A
      // frase antiga não dizia isso, e "aguardando na fila" servia tanto para quem tinha vinte
      // fichas na frente quanto para quem estava preso num envio que falhava a cada tentativa.
      if (this.status === RAW.NA_FILA) {
        const f = this.fila || {};
        if (f.motivoParada) return `A fila do Asgard está parada. ${f.motivoParada}`;
        const partes = [];
        partes.push(f.posicao && f.total
          ? `Na fila do Asgard: ${f.posicao}ª de ${f.total} aguardando o envio ao Odin.`
          : 'Na fila do Asgard, aguardando o envio ao Odin.');
        if (f.esperaMinutos) partes.push(`Envio previsto em cerca de ${this.formatarEspera(f.esperaMinutos)}.`);
        return partes.join(' ');
      }
      if (this.status === RAW.RECEIVED) {
        return 'Enviado ao motor de IA — iniciando a leitura das digitalizações…';
      }
      if (this.status === RAW.PROCESSING) {
        return 'Realizando a leitura (OCR) das digitalizações…';
      }
      if (this.status === RAW.AWAITING_BATCH) {
        return 'Leitura das digitalizações em lote — pode levar algumas horas. '
          + 'Você pode fechar e voltar depois.';
      }
      if (this.status === RAW.COMPLETED) {
        return 'Leitura (OCR) concluída. O texto das digitalizações já está disponível.';
      }
      if (this.status === RAW.ERROR) {
        return this.mensagemErro
          ? `${this.mensagemErro} Você pode executar a leitura novamente.`
          : 'A leitura (OCR) falhou. Você pode executá-la novamente.';
      }
      return '';
    },
  },
  mounted() {
    this.iniciarPolling();
  },
  beforeDestroy() {
    this.pararPolling();
  },
  methods: {
    iniciarPolling() {
      this.pararPolling();
      this.pollAtivo = true;
      this.pollTentativas = 0;
      this.agendarPoll(0);
    },
    agendarPoll(delay) {
      if (!this.pollAtivo) return;
      this.poller = setTimeout(async () => {
        this.poller = null;
        await this.consultarStatus();
        if (!this.pollAtivo) return; // consultarStatus pode ter parado (estado terminal)
        // Teto de segurança (~40 min = 80 × 30s): evita pollar indefinidamente se o processo travar.
        if (++this.pollTentativas >= 80) {
          this.pararPolling();
          return;
        }
        // Polling espaçado (30s): o OCR em lote leva minutos/horas; não precisa pingar a cada segundos.
        this.agendarPoll(30000);
      }, delay);
    },
    pararPolling() {
      this.pollAtivo = false;
      if (this.poller) { clearTimeout(this.poller); this.poller = null; }
    },
    async consultarStatus() {
      if (this.consultando) return;
      this.consultando = true;
      try {
        const snap = await OdinOcrPdfBusiness.status(this.livro, this.fichaId);
        const anterior = this.status;
        this.status = snap.status;
        this.mensagemErro = snap.mensagemErro || null;
        // Só vem no NA_FILA; zera fora dele para não exibir a posição de uma espera que já acabou.
        this.fila = snap.fila || null;

        // Emite os eventos só na TRANSIÇÃO real de "em andamento" -> terminal. Se o componente já
        // MONTA num estado terminal (ex.: OCR concluído de um run anterior), NÃO emite — senão o
        // load() do pai re-monta este componente e re-emite, entrando em loop de reloads.
        const transicaoDeAndamento = [RAW.NA_FILA, RAW.RECEIVED, RAW.PROCESSING, RAW.AWAITING_BATCH].includes(anterior);
        if (this.status === RAW.COMPLETED) {
          this.pararPolling();
          if (transicaoDeAndamento) this.$emit('concluido');
        } else if (this.status === RAW.ERROR) {
          this.pararPolling();
          if (transicaoDeAndamento) this.$emit('erro', this.mensagemErro);
        } else if (this.status === RAW.NAO_AGENDADO) {
          this.pararPolling(); // sem processo agendado: nada a acompanhar (evita polling ocioso)
        }
      } catch (err) {
        // Erro transitório de rede não deve encerrar o acompanhamento — o próximo poll tenta de novo.
      } finally {
        this.consultando = false;
      }
    },
    // Botão "Executar agora": dispara o processamento da fila na hora (sem esperar o job agendado,
    // que só roda em produção) e re-consulta o status para feedback imediato.
    async executarAgora() {
      if (this.executando) return;
      this.executando = true;
      try {
        await OdinOcrPdfBusiness.schedulerProcessar();
        await this.consultarStatus();
      } catch (err) {
        FrontBusiness.showError('', err.message);
      } finally {
        this.executando = false;
      }
    },
    // Botão "Remover da fila": tira o agendamento pendente e devolve a ficha a NAO_AGENDADO, o que
    // esconde este indicador. Só faz sentido no NA_FILA — nada foi enviado ao Odin ainda, então não
    // há OCR nem crédito a perder. Avisa o pai para ele recarregar as ações da ficha.
    async removerDaFila() {
      if (this.removendo) return;
      // confirmarExclusao, e não openConfirmacao: esta resolve 'confirm' OU 'cancel', ambos truthy.
      const ok = await FrontBusiness.confirmarExclusao('Remover da fila',
        'O OCR desta ficha sai da fila sem ser processado. Você pode gerá-lo de novo quando quiser.',
        'Remover da fila');
      if (!ok) return;
      this.removendo = true;
      try {
        await OdinOcrPdfBusiness.removerDaFila(this.livro, this.fichaId);
        this.pararPolling();
        this.status = RAW.NAO_AGENDADO;
        this.fila = null;
        FrontBusiness.showSuccess('', 'OCR removido da fila.');
        this.$emit('removido');
      } catch (err) {
        FrontBusiness.showError('Não foi possível remover da fila', err);
      } finally {
        this.removendo = false;
      }
    },
    // Espera em minutos → texto curto. Acima de uma hora o minuto exato vira ruído.
    formatarEspera(minutos) {
      if (minutos < 60) return `${minutos} min`;
      const horas = Math.round(minutos / 60);
      return horas === 1 ? '1 hora' : `${horas} horas`;
    },
  },
};
</script>

<style lang="scss" scoped>
/* Tokens locais (mesmos valores da Análise de Matrícula/ITN) — mantidos aqui para o componente
   ser autossuficiente e não depender do escopo .matricula-itn-odin. */
.odin-ocr-pdf-progresso {
  --mv-accent: #6d5efc;
  --mv-muted: #8b91a3;
  --mv-line: #e6e8f0;
  --mv-surface: #fff;
  --mv-surface-3: #f1f2f7;
  --mv-ink-soft: #4b5162;
  --mv-warn-bg: #fffaf1;
  --mv-ok: #0a7a3f;
  --mv-alert: #b45309;
  --mv-neg: #b91c1c;
}

.mv-status {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 12px;
  padding: 10px 14px;
  margin-bottom: 12px;
  border: 1px solid var(--mv-line);
  border-radius: 12px;
  background: var(--mv-surface);

  &__badge {
    display: inline-flex; align-items: center;
    font-size: 12px; font-weight: 700;
    padding: 3px 10px; border-radius: 999px;
    background: var(--mv-surface-3); color: var(--mv-ink-soft);
  }
  &__msg { font-size: 12.5px; color: var(--mv-muted); }
  &__desde { font-size: 12px; color: var(--mv-muted); white-space: nowrap; font-variant-numeric: tabular-nums; }

  &__acao {
    margin-left: auto;
    display: inline-flex; align-items: center;
    font-size: 12px; font-weight: 700;
    padding: 4px 12px; border-radius: 999px;
    border: 1px solid var(--mv-accent); color: var(--mv-accent);
    background: transparent; cursor: pointer;
    transition: background .15s ease;
    &:hover:not(:disabled) { background: rgba(109, 94, 252, .12); }
    &:disabled { opacity: .6; cursor: default; }
  }
  /* Só o PRIMEIRO botão empurra o bloco de ações para a direita; do segundo em diante vale o gap
     da linha, senão os dois ficariam separados por um vão do tamanho da sobra. */
  &__acao ~ &__acao { margin-left: 0; }
  /* Sair da fila é ação de desfazer, não de avançar: fica em cinza, sem disputar com o "Executar
     agora", que é o caminho que o usuário normalmente quer. */
  &__acao--sair {
    border-color: var(--mv-line); color: var(--mv-muted);
    &:hover:not(:disabled) { background: var(--mv-surface-3); }
  }

  &.is-proc &__badge { background: rgba(109, 94, 252, .16); color: var(--mv-accent); }
  &.is-ok &__badge { background: rgba(22, 163, 74, .14); color: var(--mv-ok); }
  &.is-err &__badge { background: rgba(239, 68, 68, .12); color: var(--mv-neg); }
  &.is-pend &__badge { background: var(--mv-warn-bg); color: var(--mv-alert); }
}

/* Falha no envio ao Odin com a ficha ainda na fila: a linha segue pendente e é retentada, então o
   indicador continua dizendo "Na fila". Este bloco é o que revela o laço. */
.mv-falha {
  padding: 10px 14px;
  margin-bottom: 12px;
  border: 1px solid var(--mv-line);
  border-left: 3px solid var(--mv-alert);
  border-radius: 12px;
  background: var(--mv-surface);

  &__head { font-size: 13px; font-weight: 700; color: var(--mv-alert); }
  &__txt { font-size: 12.5px; color: var(--mv-ink-soft); margin: 6px 0 0; }
  &__msg {
    margin: 10px 0 0;
    padding: 8px 10px;
    max-height: 140px;
    overflow: auto;
    font-size: 11.5px;
    white-space: pre-wrap;
    word-break: break-word;
    background: var(--mv-surface-3);
    border-radius: 8px;
    color: var(--mv-ink-soft);
  }
}
</style>

<!-- Dark mode (app usa body.theme-dark). Não pode ser scoped: precisa alcançar o root do componente. -->
<style lang="scss">
body.theme-dark .odin-ocr-pdf-progresso {
  --mv-accent: #8b7ffd;
  --mv-muted: #868d9c;
  --mv-line: #333a45;
  --mv-surface: #232931;
  --mv-surface-3: #2b323d;
  --mv-ink-soft: #c3c8d3;
  --mv-warn-bg: #2a2620;
  --mv-ok: #4ade80;
  --mv-alert: #fbbf24;
  --mv-neg: #f87171;
}
</style>
